import type { ReactNode } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type {
  PostMortemApproach,
  PostMortemData,
  ReviewComment,
} from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Post-mortem section (spec §8.4) — the client's self-assessment pill, the
 * approach they took, and one tile per question they answered.
 *
 * The "Approach used" tile is back, reversing spec §13 deviation #1 (LG-638):
 * it was dropped as useless, and it is not. Knowing they solved a heaps-week
 * problem by sorting is the thing a coach should catch at a glance, and it is
 * what makes the alternative-approach section meaningful — you cannot choose
 * which alternative to show without knowing which one they took. It is
 * content-gated rather than flag-gated (see `PostMortemData.approach`), so
 * today only the mock shell demos it.
 *
 * The write-up is read live off the client's session, not frozen into the
 * submission's artifact (LG-765): the coach sees whatever the client has
 * written by the time the review is opened, including a write-up finished
 * after the passing submit, and the last-edited line is what keeps that
 * honest when it changes under a comment.
 *
 * A session with no write-up renders no section at all — absent, not the red
 * dashed "Not submitted" card spec §8.4 called for, which claimed a nudge
 * nothing sends. That is the whole of this section's rule; the exception for
 * a review that already carries post-mortem comments is `SectionCard`'s to
 * enforce (LG-821), which is why `hasContent` speaks only about the write-up.
 */

const DIFFICULTY_META: Record<
  NonNullable<PostMortemData['difficulty']>,
  { color: string; bg: string; border: string }
> = {
  Again: {
    color: 'var(--cd-red)',
    bg: 'color-mix(in oklab, var(--cd-red) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-red) 30%, transparent)',
  },
  Hard: {
    color: 'var(--cd-amber)',
    bg: 'color-mix(in oklab, var(--cd-amber) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-amber) 30%, transparent)',
  },
  Good: {
    color: 'var(--cd-green)',
    bg: 'color-mix(in oklab, var(--cd-green) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-green) 30%, transparent)',
  },
  Easy: {
    color: 'var(--cd-blue)',
    bg: 'color-mix(in oklab, var(--cd-blue) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-blue) 30%, transparent)',
  },
}

/**
 * How each kind of approach label is shown, keyed on the tag rather than
 * branched on it: `source` is a union the contract expects to grow (LG-788's
 * classifier would add a third), and a lookup makes that a compile error here
 * instead of quietly rendering a machine-written label in the slot styled to
 * read as the client's own sentence.
 *
 * A catalogue name is a name, so it reads as a chip — squared off rather than
 * pill-round, because `Approach.name` is unbounded admin-authored text and a
 * pill radius on three wrapped lines is an oval. The client's own words are a
 * sentence, so they read as prose; that branch keeps `whitespace-pre-wrap`
 * even though `sanitizeOtherApproachText` collapses every whitespace run
 * today, because it matches the answer tiles below and it is the writer's
 * rule, not this column's.
 */
const APPROACH_LABEL: Record<
  PostMortemApproach['source'],
  (label: string) => ReactNode
> = {
  catalog: (label) => (
    <span className="inline-block max-w-full rounded-md border border-(--cd-border-2) px-2.5 py-0.5 text-[12.5px] font-semibold break-words text-(--cd-text-2)">
      {label}
    </span>
  ),
  client: (label) => (
    <span className="block text-[13px] leading-relaxed whitespace-pre-wrap text-(--cd-text-2)">
      {label}
    </span>
  ),
}

/**
 * One post-mortem tile: a caps label over whatever the client recorded, the
 * whole thing a click target that opens the composer on it.
 *
 * Extracted because the section has three kinds — the rating, the approach and
 * every answer — that are the same card with different contents, and the two
 * that predate this component had already drifted apart on padding without
 * anyone deciding to. The chrome lives here so the next change to it (focus
 * ring, hover state, touch target) is one edit rather than three; what varies
 * is the label, the composer target and the children.
 *
 * That includes the shouting: the heading is upper-cased in CSS here rather
 * than by each caller, which is how it was until the fourth tile would have
 * been the one to forget. Callers pass natural case — and the question bank's
 * own wording, unaltered.
 */
function PostMortemTile(props: {
  /** The heading, in natural case. `null` for an answer whose question the
   * bank retired. */
  label: string | null
  /** What the composer calls this tile. */
  target: string
  /**
   * A distinct composer key, for the one kind of tile that repeats: two
   * answers to since-retired questions both label "Answer" and would
   * otherwise collide onto one key, and rewording a question would move the
   * key of an answer that never changed.
   */
  targetKey?: string
  /** Renders the tile inert — see `PostMortemSection.readOnly`. */
  readOnly?: boolean
  children: ReactNode
}): ReactNode {
  const { label, target, targetKey, readOnly, children } = props
  const { openComposer } = useCoachingStore().review

  const chrome =
    'mb-2.5 block w-full rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left'
  const content = (
    <>
      {label !== null && (
        <span className="mb-1 block text-[10.5px] font-semibold tracking-wide uppercase text-(--cd-text-faint)">
          {label}
        </span>
      )}
      {children}
    </>
  )
  // Not a disabled button: an inert tile is not an action in any state, and a
  // disabled control would announce one that is merely unavailable.
  if (readOnly === true) return <div className={chrome}>{content}</div>
  return (
    <button
      type="button"
      onClick={() => openComposer('postmortem', target, 'critique', targetKey)}
      className={chrome}
    >
      {content}
    </button>
  )
}

export function PostMortemSection(props: {
  postMortem: PostMortemData | null
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
  /**
   * Strips every comment affordance — the section header's action and the
   * tiles' click targets — for a row with no review to hang a comment on
   * (LG-646's practice sessions). Read-only is about what is *offered*, not
   * what is shown: the write-up renders the same either way.
   */
  readOnly?: boolean
}): ReactNode {
  const { postMortem, comments = [], anchorRef, readOnly = false } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'postmortem')

  return (
    <SectionCard
      title="Post-mortem"
      commentCount={sectionComments.length}
      hasContent={postMortem !== null}
      onAddComment={
        readOnly
          ? undefined
          : () =>
              openComposer('postmortem', 'Post-mortem · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {postMortem !== null && (
        <>
          {/* Skipped, not blanked, when the client answered questions but
              never picked a rating. */}
          {postMortem.difficulty !== null && (
            <PostMortemTile
              label="Difficulty rating"
              target="Post-mortem · Difficulty rating"
              readOnly={readOnly}
            >
              <span
                className="inline-block rounded-full px-3 py-0.5 text-[12.5px] font-semibold"
                style={{
                  color: DIFFICULTY_META[postMortem.difficulty].color,
                  background: DIFFICULTY_META[postMortem.difficulty].bg,
                  border: `1px solid ${DIFFICULTY_META[postMortem.difficulty].border}`,
                }}
              >
                {postMortem.difficulty}
              </span>
            </PostMortemTile>
          )}

          {/* Absent, not empty, when the post-mortem records no approach —
              which is every post-mortem today. Whose words these are decides
              how they are shown; `APPROACH_LABEL` above owns that. */}
          {postMortem.approach !== null && (
            <PostMortemTile
              label="Approach used"
              target="Post-mortem · Approach used"
              readOnly={readOnly}
            >
              {APPROACH_LABEL[postMortem.approach.source](
                postMortem.approach.label,
              )}
            </PostMortemTile>
          )}

          {/* One tile per answered question, labelled with the bank's own
              wording — the client chooses which questions they answer, so a
              fixed pair of boxes would either invent a question they were
              never asked or drop one they answered. A question the bank no
              longer carries keeps its answer and loses only its label. */}
          {postMortem.answers.map((entry) => (
            <PostMortemTile
              key={entry.id}
              label={entry.question}
              target={`Post-mortem · ${entry.question ?? 'Answer'}`}
              // Anchored on the question id, not on the wording — see
              // `PostMortemTile`'s `targetKey`.
              targetKey={`postmortem:${entry.id}`}
              readOnly={readOnly}
            >
              <span className="block text-[13px] leading-relaxed whitespace-pre-wrap text-(--cd-text-2)">
                {entry.answer}
              </span>
            </PostMortemTile>
          ))}

          <div className="text-[11px] text-(--cd-text-faint)">
            Last edited {postMortem.lastEditedWhen}
          </div>
        </>
      )}
    </SectionCard>
  )
}
